/**
 * GTFS zip → tablo satırları (istemci, JSZip).
 * Analiz merge için /gtfs/tables/load spam'ini önler (nginx 429).
 */
import { ANALYSIS_TABLE_NAMES } from '@/composables/gtfs/gtfsFeedTypes.js'

/**
 * GTFS CSV satırı (tırnaklı alan destekli).
 * @param {string} line
 * @returns {string[]}
 */
export function parseGtfsCsvLine(line) {
  const out = []
  let cur = ''
  let inQ = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') {
        cur += '"'
        i += 1
      } else {
        inQ = !inQ
      }
    } else if (ch === ',' && !inQ) {
      out.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  out.push(cur)
  return out
}

/**
 * Zip içinde tablo dosyasını bul (kök veya alt klasör).
 * @param {import('jszip')} zip
 * @param {string} tableFile — örn. stops.txt
 */
function findZipEntry(zip, tableFile) {
  const want = tableFile.replace(/^\/+/, '').toLowerCase()
  const exact = zip.file(tableFile) || zip.file(want)
  if (exact) return exact
  const names = Object.keys(zip.files || {})
  const hit = names.find((n) => {
    if (zip.files[n]?.dir) return false
    const base = n.split('/').pop()?.toLowerCase()
    return base === want
  })
  return hit ? zip.file(hit) : null
}

/**
 * @param {string} text
 * @returns {{ columns: string[], rows: Record<string, string>[] }}
 */
export function parseGtfsCsvText(text) {
  const lines = String(text || '')
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((l) => l.trim())
  if (!lines.length) return { columns: [], rows: [] }
  const columns = parseGtfsCsvLine(lines[0]).map((c) => c.trim())
  const rows = []
  for (let i = 1; i < lines.length; i++) {
    const cols = parseGtfsCsvLine(lines[i])
    /** @type {Record<string, string>} */
    const row = {}
    columns.forEach((c, idx) => {
      row[c] = cols[idx] ?? ''
    })
    rows.push(row)
  }
  return { columns, rows }
}

/**
 * @param {File|Blob} file
 * @param {string[]} [tableNames] — uzantısız veya .txt
 * @returns {Promise<Record<string, Record<string, string>[]>>}
 */
export async function loadGtfsTablesFromZipBlob(
  file,
  tableNames = ANALYSIS_TABLE_NAMES
) {
  const JSZip = (await import('jszip')).default
  const zip = await JSZip.loadAsync(file)
  /** @type {Record<string, Record<string, string>[]>} */
  const tables = {}

  for (const raw of tableNames) {
    const short = String(raw).replace(/\.txt$/i, '')
    const tableFile = `${short}.txt`
    const entry = findZipEntry(zip, tableFile)
    if (!entry) {
      tables[short] = []
      continue
    }
    const text = await entry.async('string')
    tables[short] = parseGtfsCsvText(text).rows
  }
  return tables
}
